import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CameraIcon, CheckIcon, ImageIcon, MapPinIcon, PhoneIcon, SparklesIcon, TriangleAlertIcon } from 'lucide-react';
import { intents } from '../../data/demandes';
import type { AtnaTurnKind, Intent } from '../../types/demandes';
import { LocationPicker } from './LocationPicker';

/** Address “detected” by the simulated geolocation. */
const DETECTED_ADDRESS = '50 Avenue (Main Street), near 48 Street';
interface AtnaMessageProps {
  kind: AtnaTurnKind;
  /** Only the last assistant turn keeps its actions; history is inert. */
  live: boolean;
  intent: Intent;
  address: string;
  description: string;
  answer: string | null;
  hasPhoto: boolean;
  onConfirmIntent: () => void;
  onPickIntent: (intent: Intent) => void;
  onNotEmergency: () => void;
  onPickAddress: (address: string, source: 'position' | 'profil') => void;
  onAddPhoto: () => void;
  onSkipPhoto: () => void;
  onAnswer: (answer: string) => void;
  onSend: () => void;
  onEdit: () => void;
  onReset: () => void;
}
const primaryButton = 'rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2';
const chipButton = 'rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const quietButton = 'text-sm font-semibold text-slate-500 underline underline-offset-4 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
const eyebrow = 'flex items-center gap-2 text-sm font-bold text-ville-indigo';
export function AtnaMessage({
  kind,
  live,
  intent,
  address,
  description,
  answer,
  hasPhoto,
  onConfirmIntent,
  onPickIntent,
  onNotEmergency,
  onPickAddress,
  onAddPhoto,
  onSkipPhoto,
  onAnswer,
  onSend,
  onEdit,
  onReset
}: AtnaMessageProps) {
  if (kind === 'urgence') {
    return <div className="border-l-4 border-red-600 pl-4" data-id="element-724" data-mp="1sgejomwlwvxe">
        <p className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white" data-id="element-725" data-mp="1xepw95eadkjb">
          <TriangleAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-726" data-mp="10qa3z1jskx2i" />
          Possible emergency
        </p>
        <p className="mt-3 text-lg font-extrabold leading-snug text-ville-navy" data-id="element-727" data-mp="14qk6234gr3c3">
          This may need immediate attention.
        </p>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-728" data-mp="1efu0vqqoz03w">
          Do not wait for an online request to be processed. Call 911 if someone is in danger, or the Town’s after-hours line for a dangerous hazard on public property.
        </p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-729" data-mp="lxzwa40ghoy5">
            <a href="tel:911" className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-730" data-mp="ef8prq94b2pz">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-731" data-mp="4gsvjeesndc9" />
              Call 911
            </a>
            <a href="tel:7808263496" className="inline-flex items-center gap-2 rounded-full border border-ville-navy px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white" data-id="element-732" data-mp="fz8sod384ky">
              Town after-hours line
            </a>
            <button type="button" onClick={onNotEmergency} className={quietButton} data-id="element-733" data-mp="3hxs9239tiy8n">
              This is not an emergency, continue
            </button>
          </div>}
      </div>;
  }
  if (kind === 'comprehension') {
    return <div data-id="element-734" data-mp="38yew30mhjrj6">
        <p className={eyebrow} data-id="element-735" data-mp="38y53r6dyx8ab">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-736" data-mp="33wpmlpewvlx7" />
          Here is what I understood:
        </p>
        <p className="mt-2 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-737" data-mp="17v5ndsqn8chv">{intent.label}</p>
        <p className="mt-1 text-sm text-slate-500" data-id="element-738" data-mp="1buej3358ukg2">{intent.service}</p>
        <p className="mt-3 text-[15px] text-slate-600" data-id="element-739" data-mp="y4t2u0itwjbq">Is this the problem you want to report?</p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-2.5" data-id="element-740" data-mp="132kvwxv7mbhi">
            <button type="button" onClick={onConfirmIntent} className={primaryButton} data-id="element-741" data-mp="o63naeeenayn">
              Yes
            </button>
            {intents.filter(option => option.label !== intent.label).slice(0, 3).map(option => <button key={option.label} type="button" onClick={() => onPickIntent(option)} className={chipButton} data-id="element-742" data-mp="f5oz97a23hrg">
                  {option.label}
                </button>)}
          </div>}
      </div>;
  }
  if (kind === 'lieu') {
    return <div data-id="element-743" data-mp="29krlmw3t6jq5">
        <p className={eyebrow} data-id="element-744" data-mp="29jznbditu7w6">
          <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-745" data-mp="2eijblkkw7vrp" />
          Where is the problem?
        </p>
        {live ? <>
            <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-746" data-mp="2olomwbx3s13c">Pick the spot in one tap.</p>
            <LocationPicker onPick={onPickAddress} detectedAddress={DETECTED_ADDRESS} data-id="element-747" data-mp="2jo6b8drpb7rn" />
          </> : <p className="mt-2 text-lg font-extrabold text-ville-navy" data-id="element-748" data-mp="2xfbfxu9xeq8u">{address}</p>}
      </div>;
  }
  if (kind === 'photo') {
    return <div data-id="element-749" data-mp="2sn9vvm6inv89">
        <p className={eyebrow} data-id="element-750" data-mp="2smid5by0prze">
          <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-751" data-mp="37czb2lbg1zrj" />
          Photo
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-752" data-mp="37esjo65mau26">
          A photo would help the crew handle your request.
        </p>
        {live && <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-753" data-mp="3gdxeb1e26392">
            <button type="button" onClick={onAddPhoto} className={`inline-flex items-center gap-2 ${primaryButton}`} data-id="element-754" data-mp="3cc2fq4nii3yz">
              <CameraIcon className="h-4 w-4" aria-hidden="true" data-id="element-755" data-mp="1lz39hhrdiurw" />
              Add a photo
            </button>
            <button type="button" onClick={onSkipPhoto} className={quietButton} data-id="element-756" data-mp="1hz2w80g0gpmf">
              Continue without a photo
            </button>
          </div>}
      </div>;
  }
  if (kind === 'detail') {
    return <div data-id="element-757" data-mp="3m2xcurow0a74">
        <p className={eyebrow} data-id="element-758" data-mp="3m2nqrf6yqn6p">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-759" data-mp="3m3pb6a9vcm13" />
          One last detail
        </p>
        <p className="mt-2 text-[17px] font-bold text-ville-navy" data-id="element-760" data-mp="3r3bo0ilsc09d">{intent.question}</p>
        {live && <ul className="mt-4 flex flex-wrap gap-2" data-id="element-761" data-mp="3vxpd639utb5l">
            {intent.answers.map(option => <li key={option} data-id="element-762" data-mp="3vzmfrjqm423">
                <button type="button" onClick={() => onAnswer(option)} className={chipButton} data-id="element-763" data-mp="8v2p55c8vy6w">
                  {option}
                </button>
              </li>)}
          </ul>}
      </div>;
  }
  if (kind === 'revue') {
    return <div data-id="element-764" data-mp="ilfav0vspxem">
        <p className="text-sm font-bold uppercase tracking-wider text-ville-indigo" data-id="element-765" data-mp="ijudl3i3k0d2">Your request is ready</p>
        <h3 className="mt-2 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-766" data-mp="nltgyddu6xa0">{intent.label}</h3>

        <dl className="mt-4 space-y-3 text-sm" data-id="element-767" data-mp="rm3pnxt0k1sk">
          <div className="flex gap-3" data-id="element-768" data-mp="2c4e81o6opnt7">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-769" data-mp="2c5fpqbi7h8jw">Location</dt>
            <dd className="flex items-center gap-1.5 font-semibold text-ville-navy" data-id="element-770" data-mp="28181sgjknwmq">
              <MapPinIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-771" data-mp="232oeoct8sess" />
              {address}
            </dd>
          </div>
          <div className="flex gap-3" data-id="element-772" data-mp="1y5o9s584pi39">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-773" data-mp="2vrvls06ricpn">Description</dt>
            <dd className="font-semibold text-ville-navy" data-id="element-774" data-mp="2vtybsk7x05ud">{description}</dd>
          </div>
          {answer && <div className="flex gap-3" data-id="element-775" data-mp="2quvh9tpk9i0n">
              <dt className="w-24 shrink-0 text-slate-500" data-id="element-776" data-mp="2m1irhg95b56b">Detail</dt>
              <dd className="font-semibold text-ville-navy" data-id="element-777" data-mp="2lzpme5e6kt0f">{answer}</dd>
            </div>}
          <div className="flex gap-3" data-id="element-778" data-mp="2h0v4d5o1f8hd">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-779" data-mp="3eiv3qhfuam9f">Photo</dt>
            <dd className="font-semibold text-ville-navy" data-id="element-780" data-mp="3ek6ckvo56k7e">{hasPhoto ? '1 photo attached' : 'No photo'}</dd>
          </div>
          <div className="flex gap-3" data-id="element-781" data-mp="2i4bou07zehbw">
            <dt className="w-24 shrink-0 text-slate-500" data-id="element-782" data-mp="2i2ijqpd0o560">Contact details</dt>
            <dd className="text-slate-600" data-id="element-783" data-mp="2mv201u339eei">
              Bryon Mickelson ·{' '}
              <Link to="/my-profile" className="font-bold text-ville-indigo hover:underline" data-id="element-784" data-mp="293x5h6ekvcb1">
                Change this under My Profile
              </Link>
            </dd>
          </div>
        </dl>

        {live && <div className="mt-5 flex flex-wrap items-center gap-4" data-id="element-785" data-mp="30ubeao7qhyfu">
            <button type="button" onClick={onSend} className={primaryButton} data-id="element-786" data-mp="35tnx8xvhild5">
              Send my request
            </button>
            <button type="button" onClick={onEdit} className={quietButton} data-id="element-787" data-mp="2ruo8lq7z26gz">
              Change
            </button>
          </div>}
      </div>;
  }
  return <div data-id="element-788" data-mp="2wv2jdg0yjuip">
      <span className="grid h-11 w-11 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-789" data-mp="2wusphhcsl37q">
        <CheckIcon className="h-6 w-6" data-id="element-790" data-mp="1le2ter231rru" />
      </span>
      <h3 className="mt-3 text-xl font-extrabold tracking-tight text-ville-navy sm:text-2xl" data-id="element-791" data-mp="1lbq39qm68b3p">Request sent</h3>
      <p className="mt-1 text-sm font-semibold text-slate-500" data-id="element-792" data-mp="1cyn31jlwob65">#REQ-2842</p>
      <p className="mt-3 text-[15px] text-slate-600" data-id="element-793" data-mp="18uxfmxgyvzmp">
        The responsible department has received your request. We will let you know when its status changes.
      </p>
      {live && <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-794" data-mp="1hx6n1egxvcvv">
          <Link to="/my-requests/req-2841" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5" data-id="element-795" data-mp="u5rhlctv8fge">
            Track my request
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-796" data-mp="13xf552naxccl" />
          </Link>
          <button type="button" onClick={onReset} className={chipButton} data-id="element-797" data-mp="z3t0nxrakerv">
            Report something else
          </button>
        </div>}
    </div>;
}